import WebApp from '@twa-dev/sdk';
import type { WebAppUser } from '@twa-dev/types';

/**
 * Обёртка над @twa-dev/sdk — одно место для инициализации Mini App.
 * Вызывается один раз в main.tsx.
 */
export function initTma(): void {
  // Сообщаем Telegram, что UI готов (скрывает splash).
  WebApp.ready();
  // Раскрываем окно на полный высоту.
  WebApp.expand();
  // Подтверждение при закрытии TMA — родной диалог Telegram
  // «Изменения могут не сохраниться». Включаем глобально.
  setClosingConfirmation(true);
  // Применяем цвета темы ТГ как CSS vars. Используются в tailwind.config.ts.
  applyThemeVars();
  applyColorScheme();
  WebApp.onEvent('themeChanged', () => {
    applyThemeVars();
    applyColorScheme();
  });
}

export function getInitData(): string {
  return WebApp.initData ?? '';
}

export function getTmaUser(): WebAppUser | null {
  return WebApp.initDataUnsafe?.user ?? null;
}

export function isTmaEnv(): boolean {
  return Boolean(WebApp.initData && WebApp.initData.length > 0);
}

/**
 * Показывает Telegram BackButton и регистрирует обработчик.
 * Возвращает cleanup-функцию: снимает обработчик и скрывает кнопку.
 *
 * Используй так:
 *   useEffect(() => setBackButton(() => navigate(-1)), [navigate]);
 */
export function setBackButton(handler: () => void): () => void {
  WebApp.BackButton.onClick(handler);
  WebApp.BackButton.show();
  return () => {
    WebApp.BackButton.offClick(handler);
    WebApp.BackButton.hide();
  };
}

export function hideBackButton(): void {
  WebApp.BackButton.hide();
}

/**
 * Включает родной диалог Telegram «Изменения могут не сохраниться» при попытке
 * закрыть Mini App (свайп вниз / крестик). Текст задан Telegram'ом, кастомизации нет.
 *
 * Использование (включить, пока пользователь на странице с несохранёнными данными):
 *   useEffect(() => {
 *     setClosingConfirmation(true);
 *     return () => setClosingConfirmation(false);
 *   }, []);
 */
export function setClosingConfirmation(enabled: boolean): void {
  try {
    if (enabled) WebApp.enableClosingConfirmation();
    else WebApp.disableClosingConfirmation();
  } catch {
    // Вне Telegram — тихо игнорируем.
  }
}

/**
 * Тактильная отдача через Telegram. Безопасно вызывать вне TMA-окружения —
 * SDK сам no-op'ит, если HapticFeedback недоступен.
 *
 * Использование:
 *   haptic('light')   — обычные тапы (первичная кнопка)
 *   haptic('medium')  — переход на новый шаг, успешный submit
 *   haptic('success') — успешная генерация / оплата
 *   haptic('warning') — warning-тосты, paywall
 *   haptic('error')   — ошибка, destructive подтверждение
 */
export type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

export function haptic(kind: HapticKind): void {
  try {
    const fb = WebApp.HapticFeedback;
    if (!fb) return;
    if (kind === 'success' || kind === 'warning' || kind === 'error') {
      fb.notificationOccurred(kind);
    } else {
      fb.impactOccurred(kind);
    }
  } catch {
    // SDK может отсутствовать в браузере вне Telegram — тихо игнорируем.
  }
}

export type PhoneContactResult =
  | { kind: 'sent'; response: string }
  | { kind: 'no_data' }
  | { kind: 'cancelled' }
  | { kind: 'unsupported' };

const CONTACT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * Native Telegram popup «Отправить номер телефона?» (Bot API 6.9+). On OK Telegram
 * posts the contact into the bot chat and hands us a signed copy
 * (`contact=…&auth_date=…&hash=…`) that the API verifies like initData.
 * `no_data`: the user agreed, but Telegram did not return the signed copy in 3 s.
 * Some clients never answer the popup at all — after 60 s we give up and resolve `no_data`.
 */
export function requestPhoneContact(): Promise<PhoneContactResult> {
  return new Promise((resolve) => {
    if (!WebApp.isVersionAtLeast('6.9')) {
      resolve({ kind: 'unsupported' });
      return;
    }
    let settled = false;
    const finish = (result: PhoneContactResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };
    // Some clients never answer the popup; don't leave the button spinning forever.
    const timer = setTimeout(() => finish({ kind: 'no_data' }), CONTACT_REQUEST_TIMEOUT_MS);
    try {
      WebApp.requestContact((shared, event) => {
        if (!shared) {
          finish({ kind: 'cancelled' });
          return;
        }
        const raw = event?.status === 'sent' ? event.response : undefined;
        finish(typeof raw === 'string' && raw.length > 0 ? { kind: 'sent', response: raw } : { kind: 'no_data' });
      });
    } catch {
      // WebAppContactRequested — a popup is already open.
      finish({ kind: 'no_data' });
    }
  });
}

/** Opens a page in Telegram's in-app browser (falls back to a new tab outside Telegram). */
export function openExternalLink(url: string): void {
  try {
    WebApp.openLink(url);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/* === CSS-vars + dark-mode === */

function applyThemeVars(): void {
  const root = document.documentElement;
  const params = WebApp.themeParams;
  const map: Array<[string, string | undefined]> = [
    ['--tg-bg', params.bg_color],
    ['--tg-text', params.text_color],
    ['--tg-hint', params.hint_color],
    ['--tg-link', params.link_color],
    ['--tg-button', params.button_color],
    ['--tg-button-text', params.button_text_color],
    ['--tg-section-bg', params.secondary_bg_color],
  ];
  for (const [cssVar, value] of map) {
    if (value) root.style.setProperty(cssVar, value);
  }
}

function applyColorScheme(): void {
  // Telegram сообщает 'light' | 'dark'. Прокидываем в .dark на <html>,
  // чтобы CSS-vars (brand-muted) переключались.
  const scheme = WebApp.colorScheme;
  const root = document.documentElement;
  if (scheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}
